import React, { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Select from 'react-select'
import useSWR from 'swr'
import { api } from '../services/api'
import { Badge, Banner, Button, OverridableField, Panel, VenueSelect } from '../components'
import type { Venue } from '../types/forms'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import styles from './CupTeams.module.scss'
import formStyles from '../styles/forms.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import { convertPTInputToUTC, convertUTCToPTInput } from '../utils/timezone'
import { TIE_ROUND_LABELS, roundLabel } from '../utils/cupTeams'

interface TieHeader {
  tc_match_id: string
  ext_event_id: string | null
  start_time: string | null
  status: string | null
  match_status: string | null
  round: string | null
  group_name: string | null
  season_id: string | null
  season_name: string | null
  competition_name: string | null
  home_id: string | null
  home_name: string | null
  home_country: string | null
  away_id: string | null
  away_name: string | null
  away_country: string | null
  winner_id: string | null
  home_tie_score: number | null
  away_tie_score: number | null
  ext_home_tie_score: number | null
  ext_away_tie_score: number | null
  has_tc_override_tie_score: boolean
  tc_home_tie_score: number | null
  tc_away_tie_score: number | null
  tc_start_time: string | null
  ext_start_time: string | null
  tc_end_time: string | null
  ext_end_time: string | null
  tc_round: string | null
  ext_round: string | null
  tc_status: string | null
  ext_status: string | null
  tc_winner_id: string | null
  ext_winner_id: string | null
  sides_flipped: boolean
}

interface SetScore {
  home_games: number | null
  away_games: number | null
  home_tiebreak_points: number | null
  away_tiebreak_points: number | null
}

interface TieRubber {
  tc_match_id: string
  ext_event_id: string | null
  start_time: string | null
  status: string | null
  match_status: string | null
  rubber_home_id: string | null
  rubber_home_name: string | null
  rubber_away_id: string | null
  rubber_away_name: string | null
  home_team_type: string | null
  winner_id: string | null
  set_scores: SetScore[] | null
  attached_by_editor: boolean
}

// Scores are shown from the tie's home team's side, so a flipped tie's rubbers are swapped.
const formatSets = (sets: SetScore[] | null, flipped: boolean) =>
  (sets || [])
    .map((s) => {
      const [us, them] = flipped ? [s.away_games, s.home_games] : [s.home_games, s.away_games]
      const tb = s.home_tiebreak_points !== null && s.away_tiebreak_points !== null
        ? `(${Math.min(s.home_tiebreak_points, s.away_tiebreak_points)})`
        : ''
      return `${us ?? '-'}-${them ?? '-'}${tb}`
    })
    .join(' ')

const TIE_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'started', label: 'Started' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'live', label: 'Live' },
  { value: 'interrupted', label: 'Interrupted' },
  { value: 'ended', label: 'Ended' },
  { value: 'closed', label: 'Closed' },
  { value: 'abandoned', label: 'Abandoned' },
]

interface SelectOption {
  value: string
  label: string
}

type DetailKey = 'start' | 'end' | 'round' | 'status' | 'winner' | 'home' | 'away'

const DETAIL_FIELDS: Record<DetailKey, string> = {
  start: 'tc_start_time',
  end: 'tc_end_time',
  round: 'tc_round',
  status: 'tc_status',
  winner: 'tc_winner_competitor_id',
  home: 'tc_home_tie_score',
  away: 'tc_away_tie_score',
}

const toPayload = (key: DetailKey, value: string | null): string | number | null => {
  if (!value) return key === 'round' || key === 'status' ? '' : null
  if (key === 'start' || key === 'end') return convertPTInputToUTC(value)
  if (key === 'home' || key === 'away') return Number(value)
  return value
}

const scoreText = (score: number | null) => (score === null ? null : String(score))

const isNotPlayed = (r: TieRubber) => (r.status || '').toLowerCase() === 'cancelled'

const rubberState = (r: TieRubber) => {
  if (['live', 'started', 'interrupted'].includes((r.status || '').toLowerCase())) return { label: 'Live', variant: 'live' as const }
  return { label: r.status || '—', variant: 'secondary' as const }
}

const TieDetail: React.FC = () => {
  const { tieId } = useParams<{ tieId: string }>()

  const navigate = useNavigate()
  const [message, setMessage] = useState<{ variant: 'success' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [pastedId, setPastedId] = useState('')
  const [dirty, setDirty] = useState<Partial<Record<DetailKey, string | null>>>({})

  const [venueDirty, setVenueDirty] = useState<Venue | null | undefined>(undefined)

  const { data, error, mutate } = useSWR(tieId ? ['tie', tieId] : null, () =>
    api.get<{ tie: TieHeader; tc_venue: Venue | null; ext_venue: Venue | null; rubbers: TieRubber[] }>(`/ties/${tieId}`),
  )
  const editorial = data ? data.tie.ext_event_id === null : false
  const [adding, setAdding] = useState(false)
  const { data: candidateData, mutate: mutateCandidates } = useSWR(adding ? ['tie-candidates', tieId] : null, () =>
    api.get<{ candidates: TieRubber[] }>(`/ties/${tieId}/candidate-rubbers`),
  )
  const candidates = candidateData?.candidates || []

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true)
    setMessage(null)
    try {
      await action()
      setMessage({ variant: 'success', text: success })
      await Promise.all([mutate(), mutateCandidates()])
    } catch (err) {
      setMessage({ variant: 'error', text: (err as Error).message })
    } finally {
      setBusy(false)
    }
  }

  const attach = async (matchId: string) => {
    let result: { roster_added: number; roster_note: string | null } | null = null
    await run(async () => {
      result = await api.put<{ roster_added: number; roster_note: string | null }>(`/ties/${tieId}/rubbers/${matchId}`)
      setPastedId('')
    }, 'Rubber attached.')
    const outcome = result as { roster_added: number; roster_note: string | null } | null
    if (outcome?.roster_note) {
      setMessage({ variant: 'success', text: `Rubber attached. ${outcome.roster_note}` })
    } else if (outcome?.roster_added) {
      setMessage({
        variant: 'success',
        text: `Rubber attached. Added ${outcome.roster_added} player${outcome.roster_added === 1 ? '' : 's'} to the rosters.`,
      })
    }
  }

  const saveDetails = () =>
    run(async () => {
      const payload: Record<string, string | number | null> = {}
      for (const [key, value] of Object.entries(dirty) as [DetailKey, string | null][]) {
        payload[DETAIL_FIELDS[key]] = toPayload(key, value)
      }
      if ('home' in dirty || 'away' in dirty) {
        for (const side of ['home', 'away'] as const) {
          const value = side in dirty ? dirty[side] ?? null : valueOf(side) || null
          payload[DETAIL_FIELDS[side]] = toPayload(side, value)
        }
      }
      if (venueDirty !== undefined) payload.tc_venue_id = venueDirty?.tc_id ?? null
      await api.patch(`/ties/${tieId}`, payload)
      setDirty({})
      setVenueDirty(undefined)
    }, 'Tie details saved.')

  const detach = (matchId: string) => run(() => api.delete(`/ties/${tieId}/rubbers/${matchId}`), 'Rubber detached.')

  const deleteTie = async (teamId: string | null) => {
    setBusy(true)
    setMessage(null)
    try {
      await api.delete(`/ties/${tieId}`)
      navigate(teamId ? `/cup-teams/${teamId}` : '/cup-teams')
    } catch (err) {
      setMessage({ variant: 'error', text: (err as Error).message })
      setBusy(false)
    }
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading tie</h2>
          <p>{(error as Error).message}</p>
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading tie...</h3>
        </div>
      </div>
    )
  }

  const { tie, rubbers, tc_venue: tcVenue, ext_venue: extVenue } = data
  const flipped = tie.sides_flipped

  const played = rubbers.filter((r) => !isNotPlayed(r))
  const notPlayed = rubbers.filter(isNotPlayed)

  const rubberTable = (rows: TieRubber[], showStatus: boolean, mode: 'rubber' | 'candidate' = 'rubber') => (
    <>
      <div className={`${listTableStyles.tableHeader} ${styles.rubberHeader}`}>
        <div>#</div>
        <div>Type</div>
        <div>{tie.home_name}</div>
        <div>{tie.away_name}</div>
        <div>Score</div>
        <div>{showStatus ? 'Status' : 'Scheduled'}</div>
        <div />
      </div>
      <div className={listTableStyles.tableBody}>
        {rows.map((r, i) => {
          const state = rubberState(r)
          const [ours, theirs] = flipped
            ? [r.rubber_away_name, r.rubber_home_name]
            : [r.rubber_home_name, r.rubber_away_name]
          return (
            <div key={r.tc_match_id} className={`${listTableStyles.tableRow} ${styles.rubberRow}`}>
              <div className={listTableStyles.cellText}>{i + 1}</div>
              <div className={listTableStyles.cellText}>{r.home_team_type === 'doubles' ? 'Doubles' : 'Singles'}</div>
              <div className={listTableStyles.primaryText}>{ours || '—'}</div>
              <div className={listTableStyles.primaryText}>{theirs || '—'}</div>
              <div className={listTableStyles.cellText}>{formatSets(r.set_scores, flipped) || '—'}</div>
              <div className={listTableStyles.cellText}>
                {showStatus ? (
                  <Badge variant={state.variant} size="small">{state.label}</Badge>
                ) : (
                  r.start_time?.slice(0, 16).replace('T', ' ') || '—'
                )}
              </div>
              <div className={listTableStyles.cellActions}>
                <Link to={`/matches/${r.tc_match_id}`}>
                  <Button size="small">Match</Button>
                </Link>
                {mode === 'rubber' && r.attached_by_editor && (
                  <Button size="small" variant="secondary" disabled={busy} onClick={() => detach(r.tc_match_id)}>
                    Detach
                  </Button>
                )}
                {mode === 'candidate' && (
                  <Button size="small" disabled={busy} onClick={() => attach(r.tc_match_id)}>
                    Attach
                  </Button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )

  const teamName = (id: string | null) =>
    id === null ? null : id === tie.home_id ? tie.home_name : id === tie.away_id ? tie.away_name : null
  const tcValues: Record<DetailKey, string | null> = {
    start: tie.tc_start_time ? convertUTCToPTInput(tie.tc_start_time) : null,
    end: tie.tc_end_time ? convertUTCToPTInput(tie.tc_end_time) : null,
    round: tie.tc_round,
    status: tie.tc_status,
    winner: tie.tc_winner_id,
    home: scoreText(tie.tc_home_tie_score),
    away: scoreText(tie.tc_away_tie_score),
  }
  const extValues: Record<DetailKey, string | null> = {
    start: tie.ext_start_time ? convertUTCToPTInput(tie.ext_start_time) : null,
    end: tie.ext_end_time ? convertUTCToPTInput(tie.ext_end_time) : null,
    round: tie.ext_round,
    status: tie.ext_status,
    winner: tie.ext_winner_id,
    home: scoreText(tie.ext_home_tie_score),
    away: scoreText(tie.ext_away_tie_score),
  }
  const extDisplay: Record<DetailKey, string | null> = {
    ...extValues,
    start: extValues.start ? `${extValues.start.replace('T', ' ')} PT` : null,
    end: extValues.end ? `${extValues.end.replace('T', ' ')} PT` : null,
    round: roundLabel(extValues.round),
    status: TIE_STATUS_OPTIONS.find((o) => o.value === extValues.status)?.label || extValues.status,
    winner: teamName(extValues.winner),
  }
  const roundOptions: SelectOption[] = [
    ...new Set([...Object.keys(TIE_ROUND_LABELS), tie.ext_round, tie.tc_round].filter((v): v is string => Boolean(v))),
  ].map((value) => ({ value, label: roundLabel(value) || value }))
  const winnerOptions: SelectOption[] = [
    ...(tie.home_id ? [{ value: tie.home_id, label: tie.home_name || 'Home' }] : []),
    ...(tie.away_id ? [{ value: tie.away_id, label: tie.away_name || 'Away' }] : []),
  ]
  const tcOf = (key: DetailKey) => (key in dirty ? dirty[key] || null : tcValues[key])
  const valueOf = (key: DetailKey) => tcOf(key) ?? extValues[key] ?? ''
  const change = (key: DetailKey, value: string) => setDirty((prev) => ({ ...prev, [key]: value }))
  const revert = (key: DetailKey) =>
    setDirty((prev) => (key === 'home' || key === 'away' ? { ...prev, home: null, away: null } : { ...prev, [key]: null }))
  const field = (key: DetailKey, label: string, input: React.ReactElement) => (
    <OverridableField
      label={label}
      tcValue={tcOf(key)}
      extValue={extValues[key]}
      extDisplayValue={extDisplay[key]}
      onRevert={() => revert(key)}
      modifiedClassName={formStyles.modified}
    >
      {input}
    </OverridableField>
  )

  const venueTc = venueDirty !== undefined ? venueDirty : tcVenue
  const venueLabel = (v: Venue | null) =>
    v ? [v.name, v.complex_name, v.city].filter(Boolean).join(', ') : null

  const winnerName = tie.winner_id === tie.home_id ? tie.home_name : tie.winner_id === tie.away_id ? tie.away_name : null

  return (
    <div>
      <div className={pageStyles.pageHeader}>
        <h1>
          {tie.home_name} vs {tie.away_name}
        </h1>
        <p>
          <Badge variant="primary">Tie</Badge>{' '}
          {tie.competition_name && <Badge variant="secondary">{tie.season_name}</Badge>}{' '}
          {(tie.round || tie.group_name) && <Badge variant="neutral">{roundLabel(tie.round) || tie.group_name}</Badge>}{' '}
          {tie.ext_event_id && <code className="id-code">{tie.ext_event_id}</code>}
        </p>
      </div>

      {message && <Banner variant={message.variant} message={message.text} onDismiss={() => setMessage(null)} />}

      <div className={styles.detailsLayout}>
        <Panel margin="none">
          <Panel.Header>
            <h2>Tie details</h2>
          </Panel.Header>
          <div className={styles.fieldGrid}>
            {field(
              'start',
              'Start time (PT)',
              <input type="datetime-local" value={valueOf('start')} onChange={(e) => change('start', e.target.value)} disabled={busy} />,
            )}
            {field(
              'end',
              'End time (PT)',
              <input type="datetime-local" value={valueOf('end')} onChange={(e) => change('end', e.target.value)} disabled={busy} />,
            )}
            <OverridableField
              label="Court"
              tcValue={venueTc?.tc_id || null}
              extValue={extVenue?.tc_id || null}
              extDisplayValue={venueLabel(extVenue)}
              onRevert={() => setVenueDirty(null)}
              modifiedClassName={formStyles.modified}
            >
              <VenueSelect
                value={venueTc || extVenue}
                onChange={(venue) => setVenueDirty(venue as Venue | null)}
                isClearable
                isDisabled={busy}
                placeholder="Select court..."
                isModified={Boolean(venueTc) && venueTc?.tc_id !== extVenue?.tc_id}
              />
            </OverridableField>
            {field(
              'round',
              'Round',
              <Select<SelectOption>
                options={roundOptions}
                value={roundOptions.find((o) => o.value === valueOf('round')) || null}
                onChange={(o) => change('round', o?.value || '')}
                isClearable
                isDisabled={busy}
                placeholder="Select round..."
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />,
            )}
            {field(
              'status',
              'Status',
              <Select<SelectOption>
                options={TIE_STATUS_OPTIONS}
                value={TIE_STATUS_OPTIONS.find((o) => o.value === valueOf('status')) || null}
                onChange={(o) => change('status', o?.value || '')}
                isClearable={editorial}
                isDisabled={busy}
                placeholder="Select status..."
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />,
            )}
            {field(
              'home',
              `${tie.home_name} score`,
              <input type="number" min={0} value={valueOf('home')} onChange={(e) => change('home', e.target.value)} disabled={busy} />,
            )}
            {field(
              'away',
              `${tie.away_name} score`,
              <input type="number" min={0} value={valueOf('away')} onChange={(e) => change('away', e.target.value)} disabled={busy} />,
            )}
            {field(
              'winner',
              'Winner',
              <Select<SelectOption>
                options={winnerOptions}
                value={winnerOptions.find((o) => o.value === valueOf('winner')) || null}
                onChange={(o) => change('winner', o?.value || '')}
                isClearable
                isDisabled={busy}
                placeholder="No winner yet"
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />,
            )}
          </div>
          <div className={styles.actionButtons}>
            <Button onClick={saveDetails} disabled={busy || (Object.keys(dirty).length === 0 && venueDirty === undefined)}>Save details</Button>
            {editorial && (
              <span title={rubbers.length > 0 ? 'Detach its rubbers first.' : ''}>
                <Button variant="danger" disabled={busy || rubbers.length > 0} onClick={() => deleteTie(tie.home_id)}>
                  Delete tie
                </Button>
              </span>
            )}
          </div>
        </Panel>
        <Panel margin="none">
          <Panel.Header>
            <h2>Score</h2>
          </Panel.Header>
          <div className={styles.scoreBoard}>
            <div className={styles.scoreTeam}>
              {tie.home_id ? <Link to={`/cup-teams/${tie.home_id}`}>{tie.home_name}</Link> : tie.home_name}
              <span className={styles.scoreCountry}>{tie.home_country || '—'}</span>
            </div>
            <div className={styles.scoreNumbers}>
              {tie.home_tie_score ?? '–'} – {tie.away_tie_score ?? '–'}
            </div>
            <div className={`${styles.scoreTeam} ${styles.scoreTeamAway}`}>
              {tie.away_id ? <Link to={`/cup-teams/${tie.away_id}`}>{tie.away_name}</Link> : tie.away_name}
              <span className={styles.scoreCountry}>{tie.away_country || '—'}</span>
            </div>
          </div>
          <div className={styles.scoreMeta}>
            <span>
              {winnerName ? (
                <>
                  Winner: <strong>{winnerName}</strong>
                </>
              ) : (
                `Status: ${TIE_STATUS_OPTIONS.find((o) => o.value === tie.status)?.label || tie.status || '—'}`
              )}
            </span>
            {tie.start_time && <span>Started {tie.start_time.slice(0, 10)}</span>}
          </div>
        </Panel>
      </div>

      <Panel>
        <Panel.Header>
          <div className={styles.headerRow}>
            <h2>Rubbers</h2>
            {!adding && (
              <Button size="small" onClick={() => setAdding(true)} disabled={busy}>
                + Add rubber
              </Button>
            )}
          </div>
        </Panel.Header>
        {played.length === 0 ? (
          <p className={styles.muted}>
            {editorial ? 'No rubbers yet.' : 'Sportradar has not scheduled this tie\'s rubbers yet.'}
          </p>
        ) : (
          rubberTable(played, true)
        )}
        {adding && (
          <div className={styles.addRubbers}>
            <div className={styles.headerRow}>
              <h3>Add a rubber</h3>
              <Button size="small" variant="secondary" onClick={() => setAdding(false)} disabled={busy}>
                Done
              </Button>
            </div>
            <p className={styles.muted}>
              {editorial
                ? 'An editorial tie\'s rubbers are existing matches.'
                : 'For when Sportradar leaves a rubber out or puts it in the wrong tie. Your link overrides Sportradar\'s.'}{' '}
              Listed: matches within 4 days of the tie in which a player on either team&apos;s roster for this season plays.
            </p>
            {candidates.length === 0 ? (
              <p className={styles.muted}>
                No matches found. Add players to both teams&apos; rosters for this season, or paste a match ID below.
              </p>
            ) : (
              rubberTable(candidates, true, 'candidate')
            )}
            <div className={styles.toolbar}>
              <div className={styles.toolbarField}>
                <label htmlFor="tie-attach-id">Match ID</label>
                <input
                  id="tie-attach-id"
                  type="text"
                  value={pastedId}
                  onChange={(e) => setPastedId(e.target.value)}
                  placeholder="Paste a match UUID"
                  disabled={busy}
                />
              </div>
              <Button onClick={() => attach(pastedId.trim())} disabled={busy || !pastedId.trim()}>
                Attach
              </Button>
            </div>
          </div>
        )}
      </Panel>

      {notPlayed.length > 0 && (
        <Panel>
          <Panel.Header>
            <h2>Not played</h2>
          </Panel.Header>
          <p className={styles.muted}>
            Rubbers Sportradar cancelled: dead rubbers left once the tie was decided, and slots Sportradar re-created
            after a lineup change.
          </p>
          {rubberTable(notPlayed, false)}
        </Panel>
      )}
    </div>
  )
}

export default TieDetail
